import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-navy-950">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur-sm border-b border-navy-800/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium">
            BONESPAY
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-xs text-bone-white/60 hover:text-bone-white transition-colors uppercase tracking-wider"
          >
            <ArrowLeft size={14} />
            Back
          </Link>
        </div>
      </nav>

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <span className="font-mono text-[10px] tracking-widest uppercase text-bone-white/30">
              Legal
            </span>
            <h1 className="font-serif text-bone-white text-3xl md:text-4xl mt-2 mb-3">
              Terms &amp; Conditions
            </h1>
            <p className="font-mono text-xs text-bone-white/30">
              Last updated: March 2026
            </p>
            <div className="mt-4 w-full h-px bg-navy-800/50" />
          </div>

          <div className="space-y-8 text-bone-white/70 font-sans text-sm leading-relaxed">
            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                1. Agreement to Terms
              </h2>
              <p>
                By accessing or using BonesPay services ("the Platform"), including the merchant dashboard
                at bonespay.com, hosted checkout at checkout.bonespay.com, the BonesPay REST API, or the
                BonesPay WooCommerce plugin, you agree to be bound by these Terms and Conditions. The Platform
                is operated by BONES R&D. If you do not agree to these terms, do not use the Platform.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                2. Description of Service
              </h2>
              <p className="mb-3">
                BonesPay is a payment routing platform that enables merchants to accept credit and debit card
                payments (Visa, Mastercard, American Express, Apple Pay, Google Pay) and receive settlement
                in USDC stablecoin on the Polygon blockchain. The Platform routes customer card payments through
                third-party fiat-to-crypto onramp providers (including but not limited to Stripe Onramp, MoonPay,
                and Transak).
              </p>
              <p>
                BonesPay does not process card payments directly, does not hold merchant funds, and does not
                perform Know Your Business (KYB) verification. Card processing, Know Your Customer (KYC)
                verification, and fiat-to-USDC conversion are handled entirely by third-party onramp providers
                under their respective terms of service.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                3. Merchant Eligibility
              </h2>
              <p className="mb-3">
                To use BonesPay, merchants must:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'Provide a valid email address for account registration',
                  'Provide a valid Polygon wallet address (ERC-20 compatible, format: 0x followed by 40 hexadecimal characters) for USDC settlement',
                  'Operate a WooCommerce store, or integrate via the BonesPay REST API or supported platform plugins',
                  'Comply with all applicable laws and regulations in their jurisdiction',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3">
                BonesPay does not require merchant underwriting, government-issued identification, bank statements,
                or KYB documentation. BonesPay does not evaluate, approve, or reject merchants based on business
                category or risk classification.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                4. Payment Processing and Settlement
              </h2>
              <p className="mb-3">
                When a customer completes a card payment through BonesPay, the following occurs:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'The card payment is processed by a third-party onramp provider, not by BonesPay',
                  'The onramp provider converts the fiat payment to USDC on the Polygon blockchain',
                  'USDC is sent to a temporary deposit wallet, then forwarded to the merchant\'s self-custodial wallet',
                  'BonesPay verifies the payment on-chain by reading the Polygon blockchain and parsing USDC Transfer events',
                  'Upon successful verification, an HMAC-signed webhook is sent to the merchant\'s store to confirm payment',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3">
                Settlement is in USDC stablecoin only. BonesPay does not offer fiat settlement or fiat
                off-ramp services at this time. Merchants are responsible for managing their own
                self-custodial wallets and any subsequent conversion of USDC to fiat currency.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                5. Fees
              </h2>
              <p className="mb-3">
                Merchants are subject to the following fees:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'Onramp provider fees: 3-8% of transaction value (set by the onramp provider, not BonesPay)',
                  'BonesPay platform fee: as published on the pricing page and subject to change with notice',
                  'Polygon network gas fees: minimal (typically less than $0.01 per transaction)',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3">
                There are no rolling reserves, fund holds, volume caps, risk-based repricing, setup fees,
                monthly minimums, or hidden charges. All fees are published and transparent.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                6. Chargebacks and Disputes
              </h2>
              <p>
                Blockchain transactions are irreversible by nature. Once USDC has been transferred to the
                merchant's wallet and verified on-chain, the payment cannot be reversed through BonesPay.
                BonesPay provides proof-of-payment verification links (bonespay.com/verify/HASH) that
                display on-chain transaction data for dispute resolution purposes. Merchants are responsible
                for their own customer service and dispute resolution processes. Any chargeback disputes
                between customers and onramp providers are handled by the onramp provider and the card network,
                not by BonesPay.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                7. Payment Verification and Tolerance
              </h2>
              <p>
                BonesPay verifies each payment on the Polygon blockchain by reading transaction receipts
                and parsing USDC Transfer events. A configurable tolerance threshold (default 85%, adjustable
                5-50% per merchant) determines the minimum acceptable payment amount. If the verified on-chain
                amount is below the tolerance threshold, the order will be marked as failed. Merchants can
                configure their tolerance percentage in the dashboard settings.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                8. Self-Custodial Settlement
              </h2>
              <p>
                USDC is settled directly to the merchant's self-custodial wallet. BonesPay does not hold,
                custody, or have access to merchant funds at any point after settlement. BonesPay cannot
                freeze, seize, or withhold settled funds. Merchants are solely responsible for the security
                of their wallet private keys. Lost private keys cannot be recovered by BonesPay.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                9. API Keys and Security
              </h2>
              <p>
                Merchants are responsible for safeguarding their API keys, webhook secrets, and dashboard
                credentials. API keys should be stored as environment variables or encrypted configuration,
                never in source code repositories. BonesPay uses HMAC-SHA256 signatures for all webhook
                deliveries. Merchants should verify webhook signatures before processing payment confirmations.
                BonesPay is not liable for losses resulting from compromised API keys or failure to verify
                webhook signatures.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                10. Callback Retry Policy
              </h2>
              <p>
                When a webhook delivery fails (merchant site is unreachable), BonesPay automatically retries
                with exponential backoff: immediate, 1 minute, 5 minutes, 15 minutes, 1 hour, 4 hours,
                12 hours, and 24 hours (8 total attempts). Email alerts are sent after the third failed
                attempt. Manual retry is available via the merchant dashboard. BonesPay is not responsible
                for orders that fail to complete due to extended merchant site downtime beyond the retry
                schedule.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                11. Third-Party Services
              </h2>
              <p>
                BonesPay relies on third-party services including onramp providers (MoonPay, Transak,
                Stripe Onramp), Cloudflare (hosting, security), Supabase (database), and the Polygon
                blockchain network. BonesPay is not responsible for outages, policy changes, or actions
                taken by third-party service providers. Merchants using onramp providers are subject to
                those providers' terms of service, privacy policies, and compliance requirements.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                12. Prohibited Use
              </h2>
              <p className="mb-3">
                Merchants may not use BonesPay to:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'Process payments for goods or services that are illegal in the merchant\'s jurisdiction',
                  'Engage in money laundering, terrorist financing, or sanctions evasion',
                  'Attempt to manipulate, exploit, or abuse the payment verification system',
                  'Use automated tools to flood API endpoints beyond published rate limits',
                  'Attempt to access data belonging to other merchants',
                  'Misrepresent the nature of goods or services being sold',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                13. Limitation of Liability
              </h2>
              <p>
                BonesPay provides the Platform "as is" without warranties of any kind. BonesPay shall not
                be liable for: losses due to blockchain network delays or congestion, losses due to
                incorrect wallet addresses provided by the merchant, losses due to third-party onramp
                provider failures, losses due to compromised merchant API keys or credentials, exchange
                rate fluctuations between fiat currencies and USDC, or any indirect, incidental, or
                consequential damages arising from use of the Platform.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                14. Service Availability
              </h2>
              <p>
                BonesPay targets 99.9% uptime for all services. However, we do not guarantee uninterrupted
                service. Scheduled maintenance, third-party service outages, and blockchain network
                congestion may affect availability. BonesPay maintains a status page for real-time
                service monitoring.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                15. Account Termination
              </h2>
              <p>
                BonesPay reserves the right to suspend or terminate merchant accounts that violate these
                terms. Account termination does not affect USDC already settled to the merchant's
                self-custodial wallet. Merchants may close their account at any time through the
                dashboard settings.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                16. Changes to Terms
              </h2>
              <p>
                BonesPay may modify these Terms and Conditions at any time. Changes will be posted on
                this page with an updated revision date. Merchants will be notified via email of material
                changes. Continued use of the Platform after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                17. Governing Law
              </h2>
              <p>
                These terms shall be governed by the laws applicable to the jurisdiction of BONES R&D's
                registered entity. Any disputes arising from these terms shall be resolved through binding
                arbitration.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                18. Contact
              </h2>
              <p>
                For questions about these Terms and Conditions, contact BONES R&D at the email address
                listed in your merchant dashboard.
              </p>
            </section>
          </div>

          <div className="mt-12 border-t border-navy-800/50 pt-6">
            <p className="font-mono text-[10px] text-bone-white/20 uppercase tracking-wider">
              &copy; {new Date().getFullYear()} BonesPay &mdash; A BONES R&D Project.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
